import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, User, Power, Download, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { APP_VERSION } from "@/version";
import { API } from "@/apiConfig";
import ServerHealthBadge from "@/components/ServerHealthBadge";
import { TrialEndLine, TrialExpiredNotice } from "@/components/TrialStatus";
import { brandLogo } from "@/lib/brand";
import { LoginNumpad } from "@/components/login/LoginNumpad";

export default function Login({ onLogin }) {
  const [health, setHealth] = useState(null);
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  // Diagnostic-only: surfaces the real error on-screen when the picker can't load, since
  // production terminals rarely have dev tools open. Not a behavior change on its own.
  const [loadError, setLoadError] = useState("");
  const fetchEmployeesRef = useState(() => ({ current: null }))[0];
  // Hidden Shift+Alt+M shortcut swaps the picker for a manual User ID box —
  // the only way to type a user ID that isn't in the visible dropdown.
  const [manualEntry, setManualEntry] = useState(false);
  useEffect(() => {
    // A fresh install's very first launch can take a few extra seconds to finish backend
    // startup (Mongo index creation, seed checks) — if this fires before the backend is
    // actually listening yet, it used to fail once and silently leave the dropdown empty
    // for the rest of the session (login itself still worked once the backend caught up,
    // since that's a separate later request). Retry a few times instead of giving up once.
    let cancelled = false;
    let attempt = 0;
    const tryFetch = () => {
      if (cancelled) return;
      axios.get(`${API}/employees/login-list`).then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        if (list.length === 0 && attempt < 10) {
          attempt += 1;
          setLoadError(`Waiting for server... (attempt ${attempt}/10)`);
          setTimeout(tryFetch, 2000);
          return;
        }
        setEmployeeList(list);
        setLoadError(list.length === 0 ? "Server returned an empty user list." : "");
        const lastUserId = localStorage.getItem("login_last_user_id");
        if (lastUserId && list.some((e) => e.user_id === lastUserId)) setUserId(lastUserId);
      }).catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const msg = detail ? String(detail) : status ? `HTTP ${status}` : err.message || "Network error";
        if (attempt < 10) {
          attempt += 1;
          setLoadError(`Retrying (${attempt}/10)... last error: ${msg}`);
          setTimeout(tryFetch, 2000);
        } else {
          setLoadError(`Could not load users: ${msg}`);
        }
      });
    };
    fetchEmployeesRef.current = () => { attempt = 0; setLoadError("Retrying..."); tryFetch(); };
    tryFetch();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.altKey && e.key.toLowerCase() === "m") {
        e.preventDefault();
        setManualEntry((v) => !v);
        setUserId("");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  const [loading, setLoading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null); // null | "checking" | "available" | "downloading" | "ready" | "none" | "error"
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateVersion, setUpdateVersion] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [storeId, setStoreId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [terminalName, setTerminalName] = useState("");
  const [storeName, setStoreName] = useState(() => localStorage.getItem("login_store_name") || "");
  const [storeLogo, setStoreLogo] = useState(() => localStorage.getItem("login_store_logo") || "");
  useEffect(() => {
    axios.get(`${API}/settings/store`).then((r) => {
      const n = (r.data?.store_name || "").trim();
      const l = r.data?.logo_url || "";
      setStoreName(n); setStoreLogo(l);
      localStorage.setItem("login_store_name", n);
      localStorage.setItem("login_store_logo", l);
    }).catch(() => {});
  }, []);

  // Read Store ID / Terminal ID from Electron config (if running inside Electron)
  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return;
    api.getConfig().then((cfg) => {
      if (cfg) {
        setStoreId(cfg.store_id || "");
        setTerminalId(cfg.register_id || "");
        setTerminalName(cfg.terminal_name || cfg.register_id || "");
        localStorage.setItem("backoffice_terminal", cfg.backoffice_mode ? "1" : "0");
      }
    }).catch(() => {});
  }, []);

  // Why am I on the login screen? (Day Close logs every register out)
  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get("reason");
    if (reason === "day_closed") toast.info("Day closed — all shifts ended, every user was logged out");
    else if (reason === "idle") toast.info("Logged out automatically — no activity on this register for a while.");
    else if (reason === "logged_out") toast.info("You were logged out (End of Day closed all sessions). Please log in again.");
    if (reason) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // Auto-check for updates on mount
  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return; // Not in Electron

    setUpdateStatus("checking");

    // If the update server never responds at all (e.g. a Cloudflare Tunnel/DNS
    // hang with no error event), don't leave the spinner stuck forever — surface it.
    const timeoutId = setTimeout(() => {
      setUpdateStatus((prev) => {
        if (prev !== "checking") return prev;
        setUpdateError("No response from the update server after 20s — check the update URL / network.");
        return "error";
      });
    }, 20000);

    const handleUpdate = (data) => {
      clearTimeout(timeoutId);
      if (data.status === "available") {
        setUpdateVersion(data.version || "");
        setUpdateStatus("available");
        // Auto-start download
        api.downloadUpdate();
        setUpdateStatus("downloading");
      } else if (data.status === "up-to-date") {
        setUpdateStatus("none");
      } else if (data.status === "downloading") {
        setUpdateStatus("downloading");
        if (data.percent) setUpdateProgress(Math.round(data.percent));
      } else if (data.status === "downloaded") {
        setUpdateStatus("ready");
      } else if (data.status === "error") {
        setUpdateStatus("error");
        setUpdateError(data.message || "Update check failed");
      }
    };

    api.onUpdateStatus(handleUpdate);
    // Trigger the check
    api.checkForUpdates();

    return () => {
      clearTimeout(timeoutId);
      api.removeUpdateListeners();
    };
  }, []);

  const handleInstallUpdate = () => {
    const api = window.electronAPI;
    if (api) {
      api.installUpdate();
    }
  };

  const [pendingChange, setPendingChange] = useState(null); // { token, employee } when a default password must be replaced
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!userId) { toast.error("Select a user"); return; }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/employees/login`, {
        user_id: userId,
        password,
        station_id: terminalId || "default",
        backoffice_terminal: localStorage.getItem("backoffice_terminal") === "1",
      });

      if (response.data.must_change_password) {
        setPendingChange({ token: response.data.access_token, employee: response.data.employee });
        toast.warning("This account still uses a factory-default password. Please set a new one.");
        return;
      }
      if (!manualEntry) localStorage.setItem("login_last_user_id", userId);

      const shift = response.data.shift;
      if (shift?.privileged_login) {
        if (shift.open && !shift.is_mine) toast.success(`Logged in as ${response.data.employee.role} — ${shift.opened_by_name}'s shift stays open`);
        else if (!shift.open) toast.success(`Logged in as ${response.data.employee.role} — no shift started`);
        else toast.success("Login successful!");
      } else {
        toast.success("Login successful!");
      }
      // Electron PCs with an already-bound terminal skip the "Select This Register" picker.
      onLogin(response.data.access_token, response.data.employee,
        terminalId ? { station_id: terminalId, station_name: terminalName || terminalId } : null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/employees/change-password`,
        { current_password: password, new_password: newPassword },
        { headers: { Authorization: `Bearer ${pendingChange.token}` }, withCredentials: true });
      toast.success("Password updated");
      setPendingChange(null);
      onLogin(res.data.access_token, res.data.employee,
        terminalId ? { station_id: terminalId, station_name: terminalName || terminalId } : null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  // V10.21 — the normal sign-in form shows an on-screen numpad beside the fields, so the card is
  // wider in that state only (trial-expired / forced password change keep the original width).
  const showNumpad = health?.status !== "trial_expired" && !pendingChange;

  return (
    <div className="min-h-screen flex flex-col items-start justify-center sm:items-center bg-gradient-to-br from-blue-50 to-zinc-100 p-[0.5in]">
      <Card className={`relative w-full ${showNumpad ? "max-w-[760px]" : "max-w-[480px]"} px-12 pt-16 pb-[calc(1in+2rem)] shadow-2xl border-zinc-200`}>
        {health?.status !== "trial_expired" && <TrialEndLine health={health} />}
        <div className={`text-center mb-4 ${showNumpad ? "max-w-[380px] mx-auto" : ""}`}>
          {storeLogo ? (
            <div className="flex items-center justify-center gap-4 mb-1">
              <img src={brandLogo()} alt="Modesk POS" className="object-contain w-[55%] min-w-0" data-testid="login-store-logo" />
              <span className="w-px self-stretch bg-zinc-200" />
              <img src={`${API.replace(/\/api$/, "")}${storeLogo}`} alt={storeName || "Store logo"} className="object-contain max-h-20 w-[35%] min-w-0" onError={() => setStoreLogo("")} data-testid="login-custom-store-logo" />
            </div>
          ) : (
            <img src={brandLogo()} alt="Modesk POS" className="mx-auto mb-1 object-contain w-full" data-testid="login-store-logo" />
          )}
          {storeName && <p className="text-zinc-900 font-bold text-lg leading-tight mb-0.5" data-testid="login-store-name">{storeName}</p>}
          {health?.status !== "trial_expired" && <p className="text-zinc-500 text-sm">Enter your credentials</p>}
        </div>

        {/* Update Status Banner */}
        {updateStatus === "checking" && (
          <div className="mb-4 flex items-center justify-center gap-2 text-xs text-zinc-400" data-testid="update-checking">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Checking for updates...
          </div>
        )}
        {updateStatus === "downloading" && (
          <div className="mb-4 space-y-1.5" data-testid="update-downloading">
            <div className="flex items-center justify-center gap-2 text-xs text-blue-600">
              <Download className="w-3 h-3 animate-bounce" />
              Downloading update{updateVersion ? ` ${updateVersion}` : ""}... {updateProgress}%
            </div>
            <div className="w-full bg-blue-100 rounded-full h-1.5">
              <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${updateProgress}%` }} />
            </div>
          </div>
        )}
        {updateStatus === "ready" && (
          <div className="mb-4" data-testid="update-ready">
            <Button
              type="button"
              onClick={handleInstallUpdate}
              className="w-full h-10 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl shadow-lg animate-pulse"
              data-testid="install-update-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Update Ready{updateVersion ? ` (${updateVersion})` : ""} — Tap to Install
            </Button>
          </div>
        )}
        {updateStatus === "error" && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="update-error">
            Central is Currently Offline
          </div>
        )}

        {health?.status === "trial_expired" ? (
        <TrialExpiredNotice health={health} />
        ) : pendingChange ? (
        <form onSubmit={handleChangePassword} className="space-y-5" data-testid="force-password-change-form">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="force-password-change-notice">
            <b>{pendingChange.employee.name}</b> — set a new password before using the register. Minimum 6 characters, not a factory default.
          </div>
          <div>
            <Label htmlFor="newPassword" className="text-xs font-bold uppercase tracking-widest text-zinc-500">New Password</Label>
            <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1.5 h-12 border-zinc-200 text-lg font-mono tracking-widest" maxLength={32} required autoFocus data-testid="new-password-input" />
          </div>
          <div>
            <Label htmlFor="confirmPassword" className="text-xs font-bold uppercase tracking-widest text-zinc-500">Confirm New Password</Label>
            <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1.5 h-12 border-zinc-200 text-lg font-mono tracking-widest" maxLength={32} required data-testid="confirm-password-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button type="button" variant="outline" className="h-14 text-lg font-bold rounded-xl" onClick={() => { setPendingChange(null); setPassword(""); }} data-testid="cancel-password-change-btn">Back</Button>
            <Button type="submit" className="h-14 text-lg font-bold bg-blue-600 hover:bg-blue-700 rounded-xl" disabled={loading} data-testid="save-new-password-btn">
              {loading ? "Saving..." : "Save & Sign In"}
            </Button>
          </div>
        </form>
        ) : (
        <form onSubmit={handleLogin} className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-8 items-start" data-testid="login-form">
          <div className="space-y-5">
          <div>
            <Label htmlFor="userId" className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              User ID
            </Label>
            <div className="relative mt-1.5">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-5 h-5 z-10" />
              {manualEntry ? (
                <Input
                  id="userId"
                  type="password"
                  placeholder="User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="pl-10 h-12 border-zinc-200 text-lg font-mono tracking-widest"
                  maxLength={32}
                  required
                  autoFocus
                  autoComplete="off"
                  data-testid="login-userid-input"
                />
              ) : (
                <Select value={userId} onValueChange={setUserId}>
                  <SelectTrigger id="userId" className="pl-10 h-12 border-zinc-200 text-lg" data-testid="login-userid-select">
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {employeeList.map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id} className="text-base" data-testid={`login-userid-option-${emp.user_id}`}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {!manualEntry && loadError && employeeList.length === 0 && (
              <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-red-600" data-testid="login-userlist-error">
                <span>{loadError}</span>
                <button
                  type="button"
                  onClick={() => fetchEmployeesRef.current && fetchEmployeesRef.current()}
                  className="underline font-bold shrink-0"
                  data-testid="login-userlist-retry-btn"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              Password
            </Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-5 h-5" />
              <Input
                id="password"
                type="password"
                placeholder="------"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-12 border-zinc-200 text-lg font-mono tracking-widest"
                maxLength={32}
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-14 text-lg font-bold text-red-600 border-red-200 hover:bg-red-50 rounded-xl active:scale-95 transition-transform"
              onClick={() => {
                // Exit fullscreen first
                if (document.fullscreenElement) {
                  document.exitFullscreen().catch(() => {});
                }
                // Try to close the window (works in kiosk mode)
                window.close();
                // If still open after 300ms, try alternate close methods
                setTimeout(() => {
                  // For Edge kiosk / Chrome app mode
                  try { window.close(); } catch(e) { void 0; }
                  // Last resort: navigate to blank and try again
                  setTimeout(() => {
                    document.body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:Arial;font-size:24px;color:#666">You may now close this window.</div>';
                  }, 300);
                }, 300);
              }}
              data-testid="login-exit-button"
            >
              <Power className="w-5 h-5 mr-2" />
              Exit
            </Button>
            <Button
              type="submit"
              className="h-14 text-lg font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg active:scale-95 transition-transform"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </div>
          </div>
          <LoginNumpad
            disabled={loading}
            onDigit={(d) => setPassword((p) => (p.length >= 32 ? p : p + d))}
            onBackspace={() => setPassword((p) => p.slice(0, -1))}
            onClear={() => setPassword("")}
          />
        </form>
        )}
        <div className="absolute bottom-5 left-8 right-8 flex flex-col items-center gap-2">
          <ServerHealthBadge onHealth={setHealth} />
          <div className="w-full flex items-center justify-between text-[14px] text-zinc-400" style={{ fontFamily: "'PT Sans Narrow', sans-serif" }} data-testid="login-footer-info">
            <span data-testid="login-version">{APP_VERSION}</span>
            <span data-testid="login-store-id">{storeId ? `Store ID: (${storeId})` : ""}</span>
            <span data-testid="login-terminal-id">{terminalId ? `Terminal: ${terminalId}` : ""}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
